import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Crown, Lock, Unlock, Search, ArrowUpDown, ChevronDown, ChevronUp, BookOpen, Calendar, Mail, User, BarChart3, Menu, X, Trash2, AlertTriangle, Zap, FileText, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import ShweebumCat from "../components/ShweebumCat";
import { useAuth } from "../components/Auth/AuthProvider";
import { API } from "../lib/api";

const OwnerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantTier, setGrantTier] = useState("family");
  const [granting, setGranting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [filterTier, setFilterTier] = useState("all");
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const ADMIN_EMAIL = "boldenbrett@gmail.com";

  useEffect(() => {
    if (!user || user.email?.toLowerCase() !== ADMIN_EMAIL.toLowerCase()) {
      toast.error("Access denied - Admin only");
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [user, navigate, sortOrder]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats?user_email=${user.email}`),
        axios.get(`${API}/admin/users?user_email=${user.email}&sort=${sortOrder}`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    if (!grantEmail) {
      toast.error("Please enter an email");
      return;
    }
    setGranting(true);
    try {
      const res = await axios.post(
        `${API}/admin/set-subscription?email=${encodeURIComponent(grantEmail)}&subscription=${grantTier}&user_email=${user.email}`
      );
      toast.success(res.data.message);
      setGrantEmail("");
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update subscription");
    } finally {
      setGranting(false);
    }
  };

  const handleQuickGrant = async (email, newTier) => {
    try {
      await axios.post(
        `${API}/admin/set-subscription?email=${encodeURIComponent(email)}&subscription=${newTier}&user_email=${user.email}`
      );
      toast.success(`Updated ${email} to ${newTier}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleLockToggle = async (userId, isCurrentlyLocked) => {
    try {
      const action = isCurrentlyLocked ? 'unlock' : 'lock';
      await axios.post(`${API}/admin/user/${userId}/${action}?user_email=${user.email}`);
      toast.success(`Account ${action}ed successfully`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update account status");
    }
  };

  const fetchUserDetails = async (userId) => {
    if (userDetails[userId]) {
      setExpandedUser(expandedUser === userId ? null : userId);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/user/${userId}?user_email=${user.email}`);
      setUserDetails(prev => ({ ...prev, [userId]: res.data }));
      setExpandedUser(userId);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const handleDeleteUser = async (userToDelete) => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${API}/admin/users/${userToDelete.id}?user_email=${user.email}`
      );
      toast.success(res.data.message || `Deleted ${userToDelete.email}`);
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = filterTier === 'all' || (u.subscription || 'free') === filterTier;
    return matchesSearch && matchesTier;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  if (!user || user.email?.toLowerCase() !== ADMIN_EMAIL.toLowerCase()) {
    return null;
  }

  const tierColors = {
    free: "bg-slate-700 text-slate-300",
    pro: "bg-slate-800/60 text-cyan-400",
    author: "bg-purple-900/50 text-purple-400",
    family: "bg-green-900/50 text-green-400",
    promo: "bg-amber-900/50 text-amber-400"
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShweebumCat size="sm" />
            <span className="font-literary text-xl">Shweebum Admin</span>
            <Badge className="bg-red-500 text-white">OWNER ONLY</Badge>
          </div>
          <Button variant="ghost" className="text-white" onClick={() => navigate("/dashboard")}>
            Back to App
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">Loading admin data...</div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-6">
                  <div className="text-3xl font-bold text-white">{stats?.total_users || 0}</div>
                  <div className="text-muted-foreground text-sm">Total Users</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-6">
                  <div className="text-3xl font-bold text-green-400">${stats?.monthly_revenue || 0}</div>
                  <div className="text-muted-foreground text-sm">Monthly Revenue</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-6">
                  <div className="text-3xl font-bold text-cyan-400">{stats?.by_subscription?.pro || 0}</div>
                  <div className="text-muted-foreground text-sm">Pro Subscribers</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-6">
                  <div className="text-3xl font-bold text-purple-400">{stats?.by_subscription?.author || 0}</div>
                  <div className="text-muted-foreground text-sm">Author Subscribers</div>
                </CardContent>
              </Card>
            </div>

            {/* Grant Access Card */}
            <Card className="bg-slate-800 border-slate-700 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Crown className="w-5 h-5 text-cyan-400" />
                  Grant User Access
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Give any user a subscription tier (including free Family access)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGrantAccess} className="flex gap-4 items-end flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-white">User Email</Label>
                    <Input
                      type="email"
                      placeholder="user@email.com"
                      value={grantEmail}
                      onChange={(e) => setGrantEmail(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      data-testid="admin-grant-email"
                    />
                  </div>
                  <div className="w-[180px]">
                    <Label className="text-white">Subscription Tier</Label>
                    <Select value={grantTier} onValueChange={setGrantTier}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="pro">Pro ($9.99)</SelectItem>
                        <SelectItem value="author">Author ($19.99)</SelectItem>
                        <SelectItem value="family">Family (Free Full Access)</SelectItem>
                        <SelectItem value="promo">Promo (7-Day Trial)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    type="submit" 
                    className="bg-cyan-600 hover:bg-cyan-700"
                    disabled={granting}
                    data-testid="admin-grant-btn"
                  >
                    {granting ? "Granting..." : "Grant Access"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Users Card */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <CardTitle className="text-white">All Users ({filteredUsers.length})</CardTitle>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Search */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 bg-slate-700 border-slate-600 text-white w-full"
                        data-testid="admin-search-input"
                      />
                    </div>
                    {/* Sort */}
                    <Select value={sortOrder} onValueChange={setSortOrder}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white w-full sm:w-[150px]">
                        <ArrowUpDown className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="newest">Newest First</SelectItem>
                        <SelectItem value="oldest">Oldest First</SelectItem>
                      </SelectContent>
                    </Select>
                    {/* Filter by Tier */}
                    <Select value={filterTier} onValueChange={setFilterTier}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white w-full sm:w-[130px]">
                        <SelectValue placeholder="All Tiers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Tiers</SelectItem>
                        <SelectItem value="free">Free Only</SelectItem>
                        <SelectItem value="pro">Pro Only</SelectItem>
                        <SelectItem value="storyteller">Storyteller</SelectItem>
                        <SelectItem value="author">Author Only</SelectItem>
                        <SelectItem value="family">Family Only</SelectItem>
                        <SelectItem value="promo">Promo Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {filteredUsers.map((u) => (
                    <div 
                      key={u.id} 
                      className={`bg-slate-900/50 rounded-lg border border-slate-700 ${u.is_locked ? 'opacity-60' : ''}`}
                    >
                      {/* User Header - Always visible */}
                      <div 
                        className="p-4 cursor-pointer"
                        onClick={() => fetchUserDetails(u.id)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {expandedUser === u.id ? 
                              <ChevronUp className="w-4 h-4 text-slate-400" /> : 
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            }
                            <div>
                              <div className="text-white font-medium flex items-center gap-2">
                                {u.name}
                                {u.is_locked && <Lock className="w-3 h-3 text-red-400" />}
                              </div>
                              <div className="text-slate-400 text-xs">{u.email}</div>
                            </div>
                          </div>
                          <Badge className={tierColors[u.subscription] || tierColors.free}>
                            {u.subscription || 'free'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-400 mt-2">
                          <span><Calendar className="w-3 h-3 inline mr-1" />{formatDate(u.created_at)}</span>
                          <span><BookOpen className="w-3 h-3 inline mr-1" />{u.project_count || 0} projects</span>
                        </div>
                      </div>
                      
                      {/* Actions - Always visible */}
                      <div className="px-4 pb-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Select 
                          value={u.subscription || 'free'} 
                          onValueChange={(val) => handleQuickGrant(u.email, val)}
                        >
                          <SelectTrigger className="flex-1 h-9 bg-slate-700 border-slate-600 text-white text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="author">Author</SelectItem>
                            <SelectItem value="family">Family</SelectItem>
                            <SelectItem value="promo">Promo</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="outline"
                          className={`${u.is_locked ? "text-green-400 border-green-500/30 hover:bg-green-500/10" : "text-red-400 border-red-500/30 hover:bg-red-500/10"}`}
                          onClick={() => handleLockToggle(u.id, u.is_locked)}
                          data-testid={`admin-lock-btn-${u.id}`}
                        >
                          {u.is_locked ? <><Unlock className="w-4 h-4 mr-1" /> Unlock</> : <><Lock className="w-4 h-4 mr-1" /> Lock</>}
                        </Button>
                      </div>
                      
                      {/* Expanded Details */}
                      {expandedUser === u.id && userDetails[u.id] && (
                        <div className="px-4 pb-4 pt-2 border-t border-slate-700 space-y-3">
                          <div>
                            <h4 className="text-cyan-400 text-xs font-medium mb-1">Usage Stats</h4>
                            <div className="text-xs text-slate-300 space-y-1">
                              <p>Chapters This Month: <span className="text-white">{userDetails[u.id]?.usage?.chapters_this_month ?? 0}</span></p>
                              <p>Chapters Total: <span className="text-white">{userDetails[u.id]?.usage?.chapters_total ?? 0}</span></p>
                              <p>Books This Month: <span className="text-white">{userDetails[u.id]?.usage?.books_this_month ?? 0}</span></p>
                              <p>Words This Month: <span className="text-white">{(userDetails[u.id]?.usage?.words_this_month ?? 0).toLocaleString()}</span></p>
                              <p>Total Words: <span className="text-white">{(userDetails[u.id]?.usage?.words_total ?? 0).toLocaleString()}</span></p>
                              <p>Last Login: {formatTimeAgo(u.last_login)}</p>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-cyan-400 text-xs font-medium mb-1">Projects ({userDetails[u.id]?.project_count || 0})</h4>
                            <div className="text-xs text-slate-300 space-y-1.5 max-h-[120px] overflow-y-auto">
                              {userDetails[u.id]?.projects?.length > 0 ? (
                                userDetails[u.id].projects.map((p, idx) => (
                                  <div key={idx} className="flex items-center justify-between">
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-white">{p.title || 'Untitled'}</p>
                                      <span className="text-slate-400">{(p.word_count || 0).toLocaleString()} words, {p.chapter_count || 0} ch</span>
                                    </div>
                                    <a href={`/writing/${p.id}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                      <Eye className="w-3 h-3 text-slate-400" />
                                    </a>
                                  </div>
                                ))
                              ) : (
                                <p className="text-slate-400 italic">No projects yet</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700 text-muted-foreground">
                        <th className="text-left py-3 px-2">User</th>
                        <th className="text-left py-3 px-2 hidden md:table-cell">Subscription</th>
                        <th className="text-left py-3 px-2 hidden lg:table-cell">Joined</th>
                        <th className="text-left py-3 px-2 hidden lg:table-cell">Last Login</th>
                        <th className="text-left py-3 px-2 hidden md:table-cell">Stats</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <>
                          <tr 
                            key={u.id} 
                            className={`border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer ${u.is_locked ? 'opacity-60' : ''}`}
                            onClick={() => fetchUserDetails(u.id)}
                          >
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                {expandedUser === u.id ? 
                                  <ChevronUp className="w-4 h-4 text-slate-400" /> : 
                                  <ChevronDown className="w-4 h-4 text-slate-400" />
                                }
                                <div>
                                  <div className="text-white font-medium flex items-center gap-2">
                                    {u.name}
                                    {u.is_locked && <Lock className="w-3 h-3 text-red-400" />}
                                  </div>
                                  <div className="text-slate-400 text-xs">{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-2 hidden md:table-cell">
                              <Badge className={tierColors[u.subscription] || tierColors.free}>
                                {u.subscription || 'free'}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-slate-400 hidden lg:table-cell">
                              {formatDate(u.created_at)}
                            </td>
                            <td className="py-3 px-2 text-slate-400 hidden lg:table-cell">
                              {formatTimeAgo(u.last_login)}
                            </td>
                            <td className="py-3 px-2 hidden md:table-cell">
                              <div className="flex items-center gap-3 text-xs text-slate-400">
                                <span title="Projects"><BookOpen className="w-3 h-3 inline mr-1" />{u.project_count || 0}</span>
                                <span title="Chapters this month"><Zap className="w-3 h-3 inline mr-1" />{u.chapters_this_month || 0} ch</span>
                                <span title="Words this month"><BarChart3 className="w-3 h-3 inline mr-1" />{(u.words_used || 0).toLocaleString()} w</span>
                              </div>
                            </td>
                            <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-2">
                                <Select 
                                  value={u.subscription || 'free'} 
                                  onValueChange={(val) => handleQuickGrant(u.email, val)}
                                >
                                  <SelectTrigger className="w-[100px] h-8 bg-slate-700 border-slate-600 text-white text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="free">Free</SelectItem>
                                    <SelectItem value="pro">Pro</SelectItem>
                                    <SelectItem value="author">Author</SelectItem>
                                    <SelectItem value="family">Family</SelectItem>
                                    <SelectItem value="promo">Promo</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className={u.is_locked ? "text-green-400 hover:text-green-300" : "text-red-400 hover:text-red-300"}
                                  onClick={() => handleLockToggle(u.id, u.is_locked)}
                                  title={u.is_locked ? "Unlock Account" : "Lock Account"}
                                  data-testid={`admin-lock-btn-${u.id}`}
                                >
                                  {u.is_locked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                                </Button>
                                {/* Delete button - only for free accounts */}
                                {(!u.subscription || u.subscription === 'free') && u.email !== ADMIN_EMAIL && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                    onClick={() => setDeleteConfirm(u)}
                                    title="Delete Account"
                                    data-testid={`admin-delete-btn-${u.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {/* Expanded Details Row */}
                          {expandedUser === u.id && userDetails[u.id] && (
                            <tr key={`${u.id}-details`} className="bg-slate-900/50">
                              <td colSpan="6" className="py-4 px-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                  {/* User Info */}
                                  <div className="space-y-2">
                                    <h4 className="text-cyan-400 font-medium flex items-center gap-2">
                                      <User className="w-4 h-4" /> User Info
                                    </h4>
                                    <div className="text-sm space-y-1 text-slate-300">
                                      <p><Mail className="w-3 h-3 inline mr-2" />{u.email}</p>
                                      <p><Calendar className="w-3 h-3 inline mr-2" />Joined: {formatDate(u.created_at)}</p>
                                      <p><Calendar className="w-3 h-3 inline mr-2" />Last Login: {formatTimeAgo(u.last_login)}</p>
                                    </div>
                                  </div>
                                  {/* Usage Stats */}
                                  <div className="space-y-2">
                                    <h4 className="text-cyan-400 font-medium flex items-center gap-2">
                                      <BarChart3 className="w-4 h-4" /> Usage Stats
                                    </h4>
                                    <div className="text-sm space-y-1 text-slate-300">
                                      <p>Chapters This Month: <span className="text-white font-medium">{userDetails[u.id]?.usage?.chapters_this_month ?? u.chapters_this_month ?? 0}</span></p>
                                      <p>Chapters Total: <span className="text-white font-medium">{userDetails[u.id]?.usage?.chapters_total ?? u.chapters_total ?? 0}</span></p>
                                      <p>Books This Month: <span className="text-white font-medium">{userDetails[u.id]?.usage?.books_this_month ?? u.books_this_month ?? 0}</span></p>
                                      <p>Words This Month: <span className="text-white font-medium">{(userDetails[u.id]?.usage?.words_this_month ?? u.words_used ?? 0).toLocaleString()}</span></p>
                                      <p>Total Words: <span className="text-white font-medium">{(userDetails[u.id]?.usage?.words_total ?? u.words_total ?? 0).toLocaleString()}</span></p>
                                      <p>Projects: <span className="text-white font-medium">{userDetails[u.id]?.project_count || 0}</span></p>
                                    </div>
                                  </div>
                                  {/* Projects */}
                                  <div className="space-y-2">
                                    <h4 className="text-cyan-400 font-medium flex items-center gap-2">
                                      <BookOpen className="w-4 h-4" /> Projects ({userDetails[u.id]?.project_count || 0})
                                    </h4>
                                    <div className="text-sm space-y-2 text-slate-300 max-h-[200px] overflow-y-auto">
                                      {userDetails[u.id]?.projects?.length > 0 ? (
                                        userDetails[u.id].projects.map((p, idx) => (
                                          <div key={idx} className="flex items-start justify-between gap-2 py-1.5 border-b border-slate-700/50 last:border-0">
                                            <div className="min-w-0 flex-1">
                                              <p className="truncate text-white text-xs font-medium">{p.title || 'Untitled'}</p>
                                              <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                                                <span>{(p.word_count || 0).toLocaleString()} words</span>
                                                <span>{p.chapter_count || 0} chapters</span>
                                                {p.genre && <span>{p.genre}</span>}
                                              </div>
                                            </div>
                                            <a href={`/writing/${p.id}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400 hover:text-cyan-400" title="View project">
                                                <Eye className="w-3 h-3" />
                                              </Button>
                                            </a>
                                          </div>
                                        ))
                                      ) : (
                                        <p className="text-slate-400 italic text-xs">No projects yet</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete User Account
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to delete <span className="text-white font-medium">{deleteConfirm?.email}</span>?
              <br /><br />
              This will permanently delete:
              <ul className="list-disc list-inside mt-2 text-slate-300">
                <li>The user account</li>
                <li>All their projects and content</li>
              </ul>
              <br />
              <span className="text-red-400 font-medium">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteUser(deleteConfirm)}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              {deleting ? "Deleting..." : "Delete Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OwnerDashboard;
